import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { AdminUser, Person, Role, User } from '@crmsy/shared';
import { adminInvite, adminRemoveUser, adminSendDigest, adminUpdateUser, adminUsers, api } from '../lib/api.js';
import { db } from '../lib/db.js';
import { toast } from '../lib/toast.js';
import { useT } from '../i18n/index.js';

interface Health {
  lastPollAt: string | null;
  lastPollOk: boolean;
  lastError: string | null;
  errorCount: number;
  lastWriteAt: string | null;
  logDataSourceId: string | null;
  cursor: string | null;
  rev: number;
  contacts: number;
  live: boolean;
  subscribers: number;
  digestFailures: { email: string; failures: number; error: string | null }[];
}

const field = 'rounded-lg border border-line bg-surface px-3 py-1.5 text-sm outline-none focus:border-accent';

/** Admin (§6.6): users (invite, role, Notion person, remove, run digest) and sync health. */
export function AdminView({ me }: { me: User }) {
  const t = useT();
  const people = useLiveQuery(() => db.people.toArray(), [], [] as Person[]);
  const [health, setHealth] = useState<Health | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<Role>('member');
  const [person, setPerson] = useState('');
  const [confirmRemove, setConfirmRemove] = useState<AdminUser | null>(null);

  const loadHealth = () => api<Health>('/admin/sync-health').then(setHealth).catch(() => setHealth(null));
  const loadUsers = () => adminUsers().then(setUsers).catch((e: unknown) => toast(String(e instanceof Error ? e.message : e), { kind: 'error' }));
  useEffect(() => {
    void loadHealth();
    void loadUsers();
    const timer = setInterval(loadHealth, 5000);
    return () => clearInterval(timer);
  }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const invite = () =>
    run(async () => {
      const u = await adminInvite({ email: email.trim(), role, notionUserId: person || null });
      toast(t('admin.invited', { email: u.email }));
      setEmail('');
      setPerson('');
      setRole('member');
      await loadUsers();
    });

  const update = (u: AdminUser, body: { role?: Role; notionUserId?: string | null }) =>
    run(async () => {
      await adminUpdateUser(u.id, body);
      await loadUsers();
    });

  const remove = (u: AdminUser) =>
    run(async () => {
      await adminRemoveUser(u.id);
      toast(t('admin.removed', { email: u.email }));
      await loadUsers();
    });

  const sendDigest = (u: AdminUser) =>
    run(async () => {
      const r = await adminSendDigest(u.id);
      toast(r.sent ? t('admin.digestSent', { email: u.email }) : t('admin.digestNotSent', { reason: r.error ?? r.reason ?? '' }), { kind: r.sent ? 'info' : 'error' });
      await loadUsers();
    });

  const personName = (id: string | null) => (id ? (people.find((p) => p.id === id)?.name ?? id) : null);

  /** §6.6: the app sends no email; this puts a ready message on the clipboard for the admin to send however they like. */
  const copyInvite = async (u: AdminUser) => {
    const person = personName(u.notionUserId);
    const text = t(person ? 'admin.inviteText' : 'admin.inviteTextUnmapped', { name: u.name.split(' ')[0] ?? u.name, url: window.location.origin, email: u.email, person: person ?? '', admin: me.name });
    try {
      await navigator.clipboard.writeText(text);
      toast(t('admin.inviteCopied', { email: u.email }));
    } catch {
      toast(text, { kind: 'info', ttl: 0 }); // no clipboard (permissions): show it to copy by hand
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4 md:p-6">
      <h1 className="text-lg font-semibold">{t('admin.title')}</h1>

      {health && health.digestFailures.length > 0 && (
        <div className="mt-3 rounded-lg border border-danger bg-danger-soft px-3 py-2 text-sm text-danger" role="alert">
          {t('admin.digestBanner', { list: health.digestFailures.map((f) => `${f.email} (${f.failures}×)`).join(', ') })}
        </div>
      )}

      <h2 className="mt-5 text-xs font-semibold uppercase tracking-wide text-muted">{t('admin.users')}</h2>
      <table className="mt-2 w-full max-w-4xl text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-muted">
            <th className="py-1 pr-3">{t('admin.col.user')}</th>
            <th className="py-1 pr-3">{t('admin.col.role')}</th>
            <th className="py-1 pr-3">{t('admin.col.person')}</th>
            <th className="py-1 pr-3">{t('admin.col.digest')}</th>
            <th className="py-1" />
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-t border-line" data-user={u.email}>
              <td className="py-2 pr-3">
                <div className="font-medium">{u.name}</div>
                <div className="text-xs text-muted">{u.email}</div>
              </td>
              <td className="py-2 pr-3">
                <select value={u.role} onChange={(e) => void update(u, { role: e.target.value as Role })} className={field} disabled={busy || u.id === me.id} aria-label={t('admin.col.role')}>
                  <option value="member">{t('admin.role.member')}</option>
                  <option value="admin">{t('admin.role.admin')}</option>
                </select>
              </td>
              <td className="py-2 pr-3">
                <select value={u.notionUserId ?? ''} onChange={(e) => void update(u, { notionUserId: e.target.value || null })} className={field} disabled={busy} aria-label={t('admin.col.person')}>
                  <option value="">{t('admin.noPerson')}</option>
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                  {u.notionUserId && !people.some((p) => p.id === u.notionUserId) && <option value={u.notionUserId}>{personName(u.notionUserId)}</option>}
                </select>
              </td>
              <td className="py-2 pr-3 text-xs">
                {u.digestEnabled ? t('admin.digestOn') : t('admin.digestOff')}
                {u.digestChannel === 'telegram' && u.telegramLinked && <span className="ml-1 text-muted">{t('admin.viaTelegram')}</span>}
                {u.digestFailures > 0 && <span className="ml-1 text-danger">({u.digestFailures}× {u.digestLastError})</span>}
              </td>
              <td className="py-2 text-right whitespace-nowrap">
                <button type="button" className="btn mr-1" onClick={() => void copyInvite(u)}>{t('admin.copyInvite')}</button>
                <button type="button" className="btn mr-1" onClick={() => void sendDigest(u)} disabled={busy}>{t('admin.sendDigest')}</button>
                <button type="button" className="btn" onClick={() => setConfirmRemove(u)} disabled={busy || u.id === me.id}>{t('admin.remove')}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <form
        className="mt-3 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          void invite();
        }}
      >
        <label className="block">
          <span className="text-xs font-medium text-muted">{t('admin.inviteEmail')}</span>
          <input value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className={`${field} mt-1 block w-64`} name="inviteEmail" />
        </label>
        <label className="block">
          <span className="text-xs font-medium text-muted">{t('admin.inviteRole')}</span>
          <select value={role} onChange={(e) => setRole(e.target.value as Role)} className={`${field} mt-1 block`} name="inviteRole">
            <option value="member">{t('admin.role.member')}</option>
            <option value="admin">{t('admin.role.admin')}</option>
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium text-muted">{t('admin.invitePerson')}</span>
          <select value={person} onChange={(e) => setPerson(e.target.value)} className={`${field} mt-1 block`} name="invitePerson">
            <option value="">{t('admin.noPerson')}</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </select>
        </label>
        <button type="submit" className="btn btn-primary" disabled={busy || !email.trim()}>{t('admin.inviteButton')}</button>
      </form>

      {confirmRemove && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm" role="alertdialog">
          <span className="flex-1">{t('admin.removeConfirm', { email: confirmRemove.email })}</span>
          <button type="button" className="btn" onClick={() => setConfirmRemove(null)}>{t('dialog.cancel')}</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => {
              const u = confirmRemove;
              setConfirmRemove(null);
              void remove(u);
            }}
          >
            {t('admin.remove')}
          </button>
        </div>
      )}

      <h2 className="mt-8 text-xs font-semibold uppercase tracking-wide text-muted">{t('admin.syncHealth')}</h2>
      {health ? (
        <dl className="mt-2 grid max-w-md grid-cols-[140px_1fr] gap-y-1.5 text-sm">
          <dt className="text-muted">{t('admin.source')}</dt>
          <dd>{health.live ? t('admin.notion') : t('admin.mock')}</dd>
          <dt className="text-muted">{t('admin.lastPoll')}</dt>
          <dd className={health.lastPollOk ? '' : 'text-danger'}>
            {health.lastPollAt ? health.lastPollAt.slice(11, 19) : '—'} {health.lastPollOk ? t('admin.ok') : t('admin.failed')}
          </dd>
          <dt className="text-muted">{t('admin.lastError')}</dt>
          <dd>{health.lastError ?? '—'}</dd>
          <dt className="text-muted">{t('admin.errors')}</dt>
          <dd>{health.errorCount}</dd>
          <dt className="text-muted">{t('admin.lastWrite')}</dt>
          <dd>{health.lastWriteAt ? health.lastWriteAt.slice(11, 19) : '—'}</dd>
          <dt className="text-muted">{t('admin.crmLog')}</dt>
          <dd className="truncate">{health.logDataSourceId ?? <span className="text-danger">{t('admin.notCreated')}</span>}</dd>
          <dt className="text-muted">{t('admin.cursor')}</dt>
          <dd className="truncate">{health.cursor ?? '—'}</dd>
          <dt className="text-muted">{t('admin.rev')}</dt>
          <dd>{health.rev}</dd>
          <dt className="text-muted">{t('admin.contacts')}</dt>
          <dd>{health.contacts}</dd>
          <dt className="text-muted">{t('admin.liveTabs')}</dt>
          <dd>{health.subscribers}</dd>
        </dl>
      ) : (
        <p className="mt-2 text-sm text-muted">{t('admin.loading')}</p>
      )}
      <div className="mt-4 flex gap-2">
        <button type="button" className="btn" disabled={busy} onClick={() => void run(() => api('/admin/sync', { method: 'POST' }).then(loadHealth))}>
          {t('admin.syncNow')}
        </button>
        <button type="button" className="btn" disabled={busy} onClick={() => void run(() => api('/admin/resync', { method: 'POST' }).then(loadHealth))}>
          {t('admin.resync')}
        </button>
      </div>
    </div>
  );
}
